import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import { DEFAULT_COUNTRY_CODE, BACKEND_URL, SD, RG } from '../../../../utils/constants';
import {
  filterActiveMethods,
  PAYMENT_MODE,
  isStripeButtonAvailable,
} from '../../../../utils/payments';
import { DEFAULT_CURRENCY } from '../../../../utils/currency';
import fetch from '../../../../utils/fetch.js';

const debug = require('debug')('cw:StripePaymentButtonElement');

/**
 * StripePaymentButtonElement component provides 2 cases of UX:
 *
 * Case 1: without Google reCAPTCHA
 * - A user clicks on the StripePaymentButtonElement
 * - Payment sheet popup will be opened. It can be Google Pay, Apple Pay or popup provided by a browser
 * - User selected and confirmed payment details, after that new order will be created on the backend
 * - Draft payment is created on the backend. User may see 3d secure poppup from a bank
 * - Payment completed and user redirected to a Thank you page
 *
 * Case 2: with Google reCAPTCHA
 * - A user clicks on the StripePaymentButtonElement
 * - Payment sheet popup will be opened. It can be Google Pay, Apple Pay or popup provided by a browser
 * - User selected and confirmed payment details
 * - User may see reCAPTCHA challenge. When the challenge is passed new order will be created on the backend
 * - Draft payment is created on the backend. User may see 3d secure poppup from a bank
 * - Payment completed and user redirected to a Thank you page
 */

// Helper to confirm Stripe intent in SD/RG modes.
async function confirmCardPayment(stripe, donationType, secret, data, options) {
  if (donationType === SD) {
    const { paymentIntent, error } = await stripe.confirmCardPayment(secret, data, options);
    return {
      intent: paymentIntent,
      confirmError: error,
    };
  }

  // In RG mode we use setup intent instead of payment intent.
  const { setupIntent, error } = await stripe.confirmCardSetup(secret, data, options);
  return {
    intent: setupIntent,
    confirmError: error,
  };
}

// Helper function to complete payment after the order has been created.
async function completePayment(
  orderId,
  paymentMethod,
  captchaToken,
  stripe,
  donationType,
  eventPaymentMethodId,
  resetDonationStripeButtonStates,
  onConfirmCardPaymentError,
  onConfirmCardPaymentActionError,
  onUnexpectedError,
  onSuccessPaymentCompleted,
) {
  try {
    // Create payment and receive client secret.
    const responseCreate = await fetch(`${BACKEND_URL}/commerce/payment/create/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        captchaToken,
        gateway: paymentMethod.id,
        type: paymentMethod.type,
      },
    });

    const paymentId = responseCreate.body.payment_id[0].value;
    const clientSecret = responseCreate.body.remote_id[0].value;

    // Confirm the intent without handling potential next actions (yet).
    const { intent, confirmError } = await confirmCardPayment(
      stripe,
      donationType,
      clientSecret,
      { payment_method: eventPaymentMethodId },
      { handleActions: false },
    );

    if (confirmError) {
      debug('Stripe.js could not confirm the intent.', confirmError);
      resetDonationStripeButtonStates();

      if (onConfirmCardPaymentError) {
        onConfirmCardPaymentError(confirmError);
      }
      return;
    }

    if (intent && intent.status === 'requires_action') {
      // Let Stripe.js handle the rest of the payment flow.
      const { error } = await confirmCardPayment(stripe, donationType, clientSecret);
      if (error) {
        debug('Stripe.js could not handle card.', error);
        resetDonationStripeButtonStates();
        if (onConfirmCardPaymentActionError) {
          onConfirmCardPaymentActionError(error);
        }

        return;
      }
    }

    // Inform the backend about successful payment and finalize donation on Drupal.
    await fetch(`${BACKEND_URL}/commerce/payment/capture/${orderId}/${paymentId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (onSuccessPaymentCompleted) {
      onSuccessPaymentCompleted(orderId);
    }
  } catch (error) {
    debug('Could not process Stripe payment.', error);
    resetDonationStripeButtonStates();
    if (onUnexpectedError) {
      onUnexpectedError(error);
    }
  }
}

const StripePaymentButtonElement = ({
  paymentMethodId,
  showButton,
  amount = 0,
  onSubmitDonation,
  donationType,
  captchaToken = '',
  captchaObject = null,
  orderId = null,
  withCaptcha,
  onConfirmCardPaymentError,
  onConfirmCardPaymentActionError,
  onUnexpectedError,
  onSuccessPaymentCompleted,
  onStripeButtonAvailabilityCheck,
  onStripeButtonClick,
  onCancelPayment,
  beforePaymentMethod,
  buttonStyles = {
    paymentRequestButton: {
      type: 'donate',
      height: '60px',
    },
  },
}) => {
  const stripe = useStripe();
  const [availability, setAvailability] = useState(null);
  const [isPaymentSheetSubmitted, setPaymentSheetSubmitted] = useState(false);
  const [isPaymentSheetOpened, setPaymentSheetOpened] = useState(false);
  const [isProcessingOrder, setProcessingOrder] = useState(false);
  const [eventPaymentMethodId, setEventPaymentMethodId] = useState('');

  const resetDonationStripeButtonStates = () => {
    setPaymentSheetSubmitted(false);
    setProcessingOrder(false);
    setEventPaymentMethodId('');
  };

  // Create Stripe payment object and save it. Then we will be using the same
  // object to check availability and render payment button.
  const paymentRequest = useMemo(() => {
    if (stripe) {
      return stripe.paymentRequest({
        country: DEFAULT_COUNTRY_CODE,
        currency: DEFAULT_CURRENCY.toLowerCase(),
        total: { label: 'Concern', amount: 0 },
      });
    }
    return null;
  }, [stripe]);

  // Once payment request is initialized, check availability.
  useEffect(() => {
    if (paymentRequest && !availability) {
      paymentRequest.canMakePayment().then((result) => {
        setAvailability(result);

        if (onStripeButtonAvailabilityCheck) {
          onStripeButtonAvailabilityCheck(result);
        }
      });
    }
  }, [availability, onStripeButtonAvailabilityCheck, paymentRequest]);

  const paymentMethod = filterActiveMethods({
    mode: PAYMENT_MODE,
    donationType,
    region: DEFAULT_COUNTRY_CODE,
    stripeButtonAvailability: availability,
  }).find((method) => method.id === paymentMethodId);

  // Complete donation when order is created.
  useEffect(() => {
    if (orderId && isPaymentSheetSubmitted && !isProcessingOrder) {
      setProcessingOrder(true);
      completePayment(
        orderId,
        paymentMethod,
        captchaToken,
        stripe,
        donationType,
        eventPaymentMethodId,
        resetDonationStripeButtonStates,
        onConfirmCardPaymentError,
        onConfirmCardPaymentActionError,
        onUnexpectedError,
        onSuccessPaymentCompleted,
      );
    }
  }, [
    isPaymentSheetSubmitted,
    isProcessingOrder,
    orderId,
    paymentMethod,
    captchaToken,
    stripe,
    donationType,
    eventPaymentMethodId,
    resetDonationStripeButtonStates,
    onConfirmCardPaymentError,
    onConfirmCardPaymentActionError,
    onUnexpectedError,
    onSuccessPaymentCompleted,
  ]);

  if (!showButton || !isStripeButtonAvailable(availability)) {
    // Nothing to render.
    return null;
  }

  // Before setting a callback with most recent parameters & props, delete any
  // previous callbacks to avoid multiple orders to be created on backend.
  /* eslint-disable no-underscore-dangle */
  if (paymentRequest._callbacks && paymentRequest._callbacks.paymentmethod) {
    paymentRequest._callbacks.paymentmethod = [];
  }
  /* eslint-enable no-underscore-dangle */

  /**
   * Processes the payment on backend and frontend.
   * See https://stripe.com/docs/stripe-js/elements/payment-request-button#complete-payment
   */
  paymentRequest.on('paymentmethod', async (ev) => {
    setPaymentSheetSubmitted(true);
    setPaymentSheetOpened(false);
    setEventPaymentMethodId(ev.paymentMethod.id);

    if (beforePaymentMethod) {
      beforePaymentMethod();
    }

    if (withCaptcha && captchaObject) {
      // Execute reCAPTCHA challenge.
      captchaObject.execute();

      // Hide payment popup to be able to handle reCAPTCHA challenge,
      // because of overlapping.
      ev.complete('success');
    } else {
      // Hide payment popup to display Please wait... window.
      ev.complete('success');
      try {
        await onSubmitDonation();
      } catch (_e) {
        resetDonationStripeButtonStates();
      }
    }
  });

  paymentRequest.on('cancel', () => {
    setPaymentSheetOpened(false);
    if (onCancelPayment) {
      onCancelPayment();
    }
  });

  const onClick = (e, request) => {
    e.preventDefault();

    onStripeButtonClick(paymentMethodId, () => {
      setPaymentSheetSubmitted(false);
      setPaymentSheetOpened(true);
      request.show();
    });
  };

  // We initiated payment with zero amount. Set real amount before rendering.
  if (!isPaymentSheetSubmitted && !isProcessingOrder && !isPaymentSheetOpened) {
    // Math.round(amount * 100) is important here, since sometimes JS produces
    // odd results for multiplying, for example 2086.07 * 100 = 208607.00000000003.
    paymentRequest.update({ total: { label: 'Concern', amount: Math.round(amount * 100) } });
  }

  return (
    <PaymentRequestButtonElement
      onClick={(e) => onClick(e, paymentRequest)}
      options={{
        paymentRequest,
        style: buttonStyles,
      }}
    />
  );
};

StripePaymentButtonElement.propTypes = {
  paymentMethodId: PropTypes.string.isRequired,
  showButton: PropTypes.bool.isRequired,
  amount: PropTypes.number,
  onSubmitDonation: PropTypes.func.isRequired,
  donationType: PropTypes.oneOf([SD, RG]).isRequired,
  captchaToken: PropTypes.string,
  captchaObject: PropTypes.shape({
    execute: PropTypes.func.isRequired,
  }),
  orderId: PropTypes.number,
  withCaptcha: PropTypes.bool.isRequired,
  onConfirmCardPaymentError: PropTypes.func.isRequired,
  onConfirmCardPaymentActionError: PropTypes.func.isRequired,
  onUnexpectedError: PropTypes.func.isRequired,
  onSuccessPaymentCompleted: PropTypes.func.isRequired,
  onStripeButtonAvailabilityCheck: PropTypes.func.isRequired,
  onCancelPayment: PropTypes.func.isRequired,
  onStripeButtonClick: PropTypes.func.isRequired,
  beforePaymentMethod: PropTypes.func.isRequired,
  buttonStyles: PropTypes.object,
};

export default StripePaymentButtonElement;
